<!--
  Tooltip component:
  - Floats above the UI
  - Is attached to a target element
  - Repositions itself based on the target element's position
-->

<template>
  <Teleport v-if="visible" :to="teleportTo">
    <div
      class="tool-tip"
      ref="tooltipElement"
      :style="floatingStyles"
      v-bind="$attrs"
    >
      <slot></slot>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, useTemplateRef } from 'vue';
import { useFloating, autoUpdate, flip, shift } from "@floating-ui/vue";

const props = withDefaults(
  defineProps<{
    target: HTMLElement | null;
    visible?: boolean;
    teleportTo?: string;
    placement?: 'top' | 'bottom' | 'left' | 'right';
  }>(),
  {
    visible: true,
    teleportTo: '#app',
  }
);

const emit = defineEmits<{
  (event: 'click:outside'): void;
  (event: 'update:visible', value: boolean): void;
}>();


const referenceElement = computed(() => props.target as HTMLElement);
const tooltipElement = useTemplateRef('tooltipElement');
const tooltipPlacement = computed(() => props.placement || 'bottom');

onMounted(() => {
  document.addEventListener("click", onOutsideClick, { capture: true });
});
onBeforeUnmount(() => {
  document.removeEventListener("click", onOutsideClick, { capture: true });
});

function onOutsideClick(event: MouseEvent) {
  // Check if the click is outside the tooltip
  if (tooltipElement.value && !tooltipElement.value.contains(event.target as Node)) {
    // ignore clicks on the reference element
    if (referenceElement.value && referenceElement.value.contains(event.target as Node)) {
      return;
    }
    
    emit('click:outside');
  }
}

const { floatingStyles } = useFloating(referenceElement, tooltipElement, {
  placement: tooltipPlacement,
  middleware: [
    flip(),
    shift(),
  ],
  whileElementsMounted: autoUpdate,
});
</script>

<style lang="scss"></style>